// Display form of a part-of-speech label. The pos is a technical word that repeats on every row,
// so the lean rows and the drill cue show it abbreviated to at most 5 characters and hand the full
// word to a tooltip (wire). The abbreviation is DISPLAY-ONLY: the DB, the API, the corpus, the rich
// card and the stored `cue_shown` history all keep the full word, so this map is the only place the
// short forms exist and dropping it restores the old rendering.
//
// Two pos vocabularies reach this map, and both are keyed here by the string as stored: the rich
// dictionary's free-text `sense.pos` (DE terms, always English words) and the legacy
// `PartOfSpeech` enum descriptions, which are English on EN terms but FRENCH on FR terms ("nom",
// "verbe", "adjectif"). CANON collapses both onto ONE key per grammatical concept — "adjective" and
// "adjectif" are the same label, not two — and every rendered string (abbreviation AND tooltip) is
// then looked up in the VIEWER's own locale via getRes(), keyed "pos<Canon>_label" /
// "pos<Canon>_tooltip" (resources_*.js). Before this, both the abbreviation and the tooltip were the
// raw stored word shown to every viewer regardless of THEIR locale, and for "phrase_verb_conj" /
// "verb_forms" the raw word was not even a real word, just the DB's own internal name for the card
// type. See src/test/js/pos_label_test.js.
var PosLabel = (function () {
	"use strict";

	// Char budget. 5 covers every conventional abbreviation in every locale wired up so far
	// (English, French, Russian — see resources_*.js), and bounds the gutter the label sits in.
	var MAX = 5;

	// full (lowercased, as stored) -> canonical concept, matching a "pos<Canon>_label" /
	// "pos<Canon>_tooltip" pair in the resource bundle. "interjection" and "article" are spelled
	// identically in English and French, so one entry serves each; every other French word gets its
	// own entry pointing at the SAME concept as its English counterpart.
	var CANON = {
		// English — the rich dictionary's sense.pos plus the legacy enum's English descriptions
		"noun": "Noun",
		"verb": "Verb",
		"adjective": "Adjective",
		"adverb": "Adverb",
		"phrase": "Phrase",
		// A conjugated combo persisted as its own term ("ich stehe auf") — pos "phrase_verb_conj",
		// written by RichTranslationService.conjugationEntry. Distinct from "phrase" (a typed
		// phrase) and from "conjunction", whose abbreviation this must not collide with.
		"phrase_verb_conj": "PhraseVerbConj",
		// A principal-parts card persisted as its own term ("go, went, gone") — pos "verb_forms",
		// written by RichTranslationService.verbFormsEntry. Its own concept rather than sharing
		// PhraseVerbConj with the German combo above: the two are different cards (one paradigm
		// cell versus a whole verb's forms) and a learner drilling both should see which is which.
		"verb_forms": "VerbForms",
		"preposition": "Preposition",
		"pronoun": "Pronoun",
		"conjunction": "Conjunction",
		"interjection": "Interjection",
		"numeral": "Numeral",
		"article": "Article",
		"abbreviation": "Abbreviation",
		// New for the Spanish sense layer (changeset 0136, es-term-onboarding §2.1): mi, tu, su,
		// este, ese, aquel, algún, ningún, cada, todo, mucho, poco, otro, cualquier, cierto,
		// tanto, ambos, varios — ~40 rows in the Spanish top 1,000, with no home in the English
		// list. Nearly all of them are also pronouns (`este libro` / `este es mío`), which resolves
		// only because pos is per SENSE. ⚠️ One DE row already carries pos='determiner' and, until
		// this entry existed, showed every viewer the truncated stub "deter".
		"determiner": "Determiner",
		// particle/prefix are absent from the de-en prompt's authorized list but present in the
		// corpus (10 senses); mapped so they render like any other label if they survive QA.
		"particle": "Particle",
		"prefix": "Prefix",

		// ── FORM KINDS, not parts of speech (added 2026-08-27) ───────────────────────────────
		//
		// A thin `word_form` row ("Häuser", "ging", "drew") stores its FORM KIND in `sense.pos` —
		// the same "a marker, not a part of speech" precedent phrase_verb_conj set, written by
		// RichTranslationService.persistWordForm. There are 56,193 such rows (42,095 DE, 14,098
		// EN), and until this block existed every one of them fell through to raw.slice(0, 5) and
		// showed a truncated internal English word — "prete", "prese", "decli", "plura" — to every
		// viewer in every locale. Exactly the leak §5 of conj-card-and-answer-matching.md is about,
		// on four times the rows the two card-type entries above cover.
		//
		// The kind is worth SHOWING rather than hiding: on a card cueing `ging` it says which form
		// is wanted, the same job the tense chip does on a conjugation cell. What it must not do is
		// say it in the DB's own words.
		"plural": "Plural",
		"genitive_sg": "GenitiveSg",
		// German's biggest kind by far (16,161 rows): adjective, noun, pronoun and article
		// declension all land here, so the label names the declension and not a part of speech.
		"declined": "Declined",
		"past": "Past",
		"preterite": "Preterite",
		"past_participle": "PastParticiple",
		"present_1sg": "Present1Sg",
		"present_2sg": "Present2Sg",
		"present_3sg": "Present3Sg",
		"imperative": "Imperative",
		"subjunctive_ii": "SubjunctiveII",
		"separable_particle": "SeparableParticle",
		"comparative": "Comparative",
		"superlative": "Superlative",
		"ing_form": "IngForm",
		"contraction": "Contraction",
		// One letter from Superlative's "sup" in English, and deliberately so — both are real
		// abbreviations of their word, they never co-occur on one card, and 8 rows carry this one.
		"suppletive": "Suppletive",
		"proper_noun": "ProperNoun",
		"spelling_variant": "SpellingVariant",
		"fusion": "Fusion",
		// A bucket, and named as one. It is NOT form-specific — head entries carry `other` senses
		// too — so the label says "other" and never guesses at "form".
		"other": "Other",
		// ── SPANISH FORM KINDS, not parts of speech (F2b, 2026-09-08) ───────────────────────
		//
		// The same job the German block above does, on 151,521 rows instead of 42,095 — and until
		// F2b every one of them fell through to raw.slice(0, 5) and showed "verb_", "imp_a",
		// "adj_f" to every viewer in every locale. §2.11's form-kind gate ("every kind has a
		// label in EVERY UI bundle") is what was left unclosed at F1; pack_gate.py --lang=es
		// check 2 now asserts both halves, this map and the five bundles.
		//
		// ⚠️ A part beginning with a DIGIT capitalizes its first LETTER — "1sg" is "1Sg", the
		// shape "present_3sg": "Present3Sg" already set. Rows are generated by
		// analytics/i18n/add_es_form_kind_keys.py --apply, never typed.
		// the 49 simple finite cells — the 8 synthetic tenses crossed with the 7 person slots
		"verb_pres_ind_1sg": "VerbPresInd1Sg",
		"verb_pres_ind_2sg_tu": "VerbPresInd2SgTu",
		"verb_pres_ind_2sg_vos": "VerbPresInd2SgVos",
		"verb_pres_ind_3sg": "VerbPresInd3Sg",
		"verb_pres_ind_1pl": "VerbPresInd1Pl",
		"verb_pres_ind_2pl_vosotros": "VerbPresInd2PlVosotros",
		"verb_pres_ind_3pl": "VerbPresInd3Pl",
		"verb_pret_ind_1sg": "VerbPretInd1Sg",
		"verb_pret_ind_2sg_tu": "VerbPretInd2SgTu",
		"verb_pret_ind_3sg": "VerbPretInd3Sg",
		"verb_pret_ind_1pl": "VerbPretInd1Pl",
		"verb_pret_ind_2pl_vosotros": "VerbPretInd2PlVosotros",
		"verb_pret_ind_3pl": "VerbPretInd3Pl",
		"verb_imperf_ind_1sg": "VerbImperfInd1Sg",
		"verb_imperf_ind_2sg_tu": "VerbImperfInd2SgTu",
		"verb_imperf_ind_3sg": "VerbImperfInd3Sg",
		"verb_imperf_ind_1pl": "VerbImperfInd1Pl",
		"verb_imperf_ind_2pl_vosotros": "VerbImperfInd2PlVosotros",
		"verb_imperf_ind_3pl": "VerbImperfInd3Pl",
		"verb_fut_ind_1sg": "VerbFutInd1Sg",
		"verb_fut_ind_2sg_tu": "VerbFutInd2SgTu",
		"verb_fut_ind_3sg": "VerbFutInd3Sg",
		"verb_fut_ind_1pl": "VerbFutInd1Pl",
		"verb_fut_ind_2pl_vosotros": "VerbFutInd2PlVosotros",
		"verb_fut_ind_3pl": "VerbFutInd3Pl",
		"verb_cond_1sg": "VerbCond1Sg",
		"verb_cond_2sg_tu": "VerbCond2SgTu",
		"verb_cond_3sg": "VerbCond3Sg",
		"verb_cond_1pl": "VerbCond1Pl",
		"verb_cond_2pl_vosotros": "VerbCond2PlVosotros",
		"verb_cond_3pl": "VerbCond3Pl",
		"verb_pres_subj_1sg": "VerbPresSubj1Sg",
		"verb_pres_subj_2sg_tu": "VerbPresSubj2SgTu",
		"verb_pres_subj_3sg": "VerbPresSubj3Sg",
		"verb_pres_subj_1pl": "VerbPresSubj1Pl",
		"verb_pres_subj_2pl_vosotros": "VerbPresSubj2PlVosotros",
		"verb_pres_subj_3pl": "VerbPresSubj3Pl",
		"verb_imperf_subj_ra_1sg": "VerbImperfSubjRa1Sg",
		"verb_imperf_subj_ra_2sg_tu": "VerbImperfSubjRa2SgTu",
		"verb_imperf_subj_ra_3sg": "VerbImperfSubjRa3Sg",
		"verb_imperf_subj_ra_1pl": "VerbImperfSubjRa1Pl",
		"verb_imperf_subj_ra_2pl_vosotros": "VerbImperfSubjRa2PlVosotros",
		"verb_imperf_subj_ra_3pl": "VerbImperfSubjRa3Pl",
		"verb_imperf_subj_se_1sg": "VerbImperfSubjSe1Sg",
		"verb_imperf_subj_se_2sg_tu": "VerbImperfSubjSe2SgTu",
		"verb_imperf_subj_se_3sg": "VerbImperfSubjSe3Sg",
		"verb_imperf_subj_se_1pl": "VerbImperfSubjSe1Pl",
		"verb_imperf_subj_se_2pl_vosotros": "VerbImperfSubjSe2PlVosotros",
		"verb_imperf_subj_se_3pl": "VerbImperfSubjSe3Pl",
		// the 6 affirmative imperatives — the negative one is RENDERED as no + present subjunctive
		"imp_aff_2sg_tu": "ImpAff2SgTu",
		"imp_aff_2sg_vos": "ImpAff2SgVos",
		"imp_aff_3sg_usted": "ImpAff3SgUsted",
		"imp_aff_1pl": "ImpAff1Pl",
		"imp_aff_2pl_vosotros": "ImpAff2PlVosotros",
		"imp_aff_3pl_ustedes": "ImpAff3PlUstedes",
		// non-finite
		"infinitive": "Infinitive",
		"gerund": "Gerund",
		"participle": "Participle",
		"participle_alt": "ParticipleAlt",
		// nominal
		"noun_plural": "NounPlural",
		"noun_feminine_sg": "NounFeminineSg",
		"noun_feminine_pl": "NounFemininePl",
		"noun_masc_pl_of_fem_head": "NounMascPlOfFemHead",
		// adjectival and derived
		"adj_fem_sg": "AdjFemSg",
		"adj_masc_pl": "AdjMascPl",
		"adj_fem_pl": "AdjFemPl",
		"adj_apocope": "AdjApocope",
		"adj_superlativo_absoluto": "AdjSuperlativoAbsoluto",
		"adj_superlativo_absoluto_alt": "AdjSuperlativoAbsolutoAlt",
		"adv_mente": "AdvMente",
		// determiner and numeral
		"det_fem_sg": "DetFemSg",
		"det_masc_pl": "DetMascPl",
		"det_fem_pl": "DetFemPl",
		"det_neuter": "DetNeuter",
		"det_apocope": "DetApocope",
		"num_feminine": "NumFeminine",
		"num_apocope": "NumApocope",
		// French — the legacy enum's descriptions, carried by FR terms. Same concepts as above, so
		// a French term's pos renders in the VIEWER's locale exactly like an English term's does,
		// instead of leaking raw French to every viewer the way it did before this map existed.
		"nom": "Noun",
		"verbe": "Verb",
		"adjectif": "Adjective",
		"adverbe": "Adverb",
		"pronom": "Pronoun",
		"préposition": "Preposition",
		"conjonction": "Conjunction"
	};

	// The canonical concept for a stored pos, or undefined for one CANON does not cover (a pos the
	// corpus starts emitting later) — callers degrade gracefully rather than throw.
	function canon(pos) {
		return CANON[String(pos).trim().toLowerCase()];
	}

	// The label to render, in the VIEWER's locale (getRes reads curr_locale, same as every other
	// string in the app). An unmapped pos has no resource key to read, so it is truncated to the
	// budget instead — the gutter is sized to MAX, so an unknown label degrades to a stub rather
	// than breaking the row.
	function short(pos) {
		if (!pos) {
			return "";
		}
		var raw = String(pos).trim();
		var key = canon(raw);
		return key ? getRes("pos" + key + "_label") : raw.slice(0, MAX);
	}

	// The tooltip text for a mapped pos, in the viewer's locale — "" for an unmapped one, where
	// wire() falls back to the raw stored word: still better than nothing, and there is no
	// translation to look up for a pos with no CANON entry.
	function tooltipText(pos) {
		var key = canon(pos);
		return key ? getRes("pos" + key + "_tooltip") : "";
	}

	function esc(s) {
		return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
	}

	// The rendered label, "" for a blank pos (~29k legacy definitions carry none, so callers can
	// append this unguarded). `data-pos` carries the untouched source word for wire()'s fallback —
	// see tooltipText.
	function html(pos) {
		if (!pos) {
			return "";
		}
		return "<span class='subduedClass _posClass' data-pos='" + esc(pos) + "'>"
			+ esc(short(pos)) + "</span>";
	}

	// Attach the tooltip to every pos label under `$root` (a jQuery element) whose label actually
	// differs from its tooltip text — e.g. English "noun"/"verb" already show the full word, so a
	// tooltip on them would just repeat it; a Russian label almost never does, since сущ./гл. are
	// always shorter than существительное/глагол.
	// Per element via .each, NEVER on the collection: assignTooltip measures its anchor from the
	// first element of whatever collection it is called on, so one call for a multi-sense row would
	// stack every tip onto the first label.
	function wire($root) {
		$root.find("._posClass").each(function () {
			var raw = $(this).attr("data-pos");
			var tip = tooltipText(raw) || raw;
			if (tip.toLowerCase() === String(short(raw)).toLowerCase()) {
				return;
			}
			$(this).assignTooltip(function () {
				return tip;
			});
		});
	}

	return {short: short, tooltip: tooltipText, html: html, wire: wire, MAX: MAX};
})();
